import React, { useEffect, useRef, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import {
  Platform,
  Pressable,
  Share,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { ticketChrome as ticketUi } from '../../shared/ticket/ticketChrome.styles'
import { TicketMoreActionsSheet } from '../../shared/ticket/TicketMoreActionsSheet'
import type { TicketMoreAction } from '../../shared/ticket/TicketMoreActionsSheet';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { showError, showSuccess } from '../../shared/ui/appToast';
import {
  AGENDA_DOT_FALLBACKS,
  agenda,
  agendaDotColor,
  agendaFonts,
  agendaPaperSheet,
} from '../domain/agendaTheme';
import { ResourceWeekHoursEditor } from './ResourceWeekHoursEditor';
import { ResourceServicesEditor } from './ResourceServicesEditor';
import { ResourceTimeOffEditor } from './ResourceTimeOffEditor';
import type {
  AppointmentResource,
  ResourceHourDay,
  ResourceHourShift,
  ResourceTimeOff,
  ServiceProductOption,
} from '../domain/types';
import type { ResourceTimeOffCreateInput } from './ResourceTimeOffEditor';

export type TabKey = 'horarios' | 'servicios' | 'ausencias' | 'reserva';

/** Nombres legibles de la paleta de dots — a11y no lee hex crudo. */
const SWATCH_NAMES: Record<string, string> = {
  [ticketColors.gold]: 'Verde',
  '#2563EB': 'Azul',
  '#A21CAF': 'Violeta',
  '#C2410C': 'Naranja',
  '#0E7490': 'Turquesa',
  '#B45309': 'Marrón',
  '#4D7C0F': 'Oliva',
  '#BE123C': 'Rosa',
  '#4338CA': 'Índigo',
  '#047857': 'Esmeralda',
};

export type AgendaAdminModalProps = {
  visible: boolean;
  resource: AppointmentResource | null;
  resourceIndex: number;
  isWide: boolean;
  canManage: boolean;
  agendaEmployees: Array<{ id: string; name: string; role: string }>;
  allServices: ServiceProductOption[];
  selectedServiceIds: string[];
  hoursDraft: ResourceHourDay[];
  timeOffList: ResourceTimeOff[];
  timeOffLoading: boolean;
  savingHours: boolean;
  savingServices: boolean;
  creatingTimeOff: boolean;
  deletingTimeOff: boolean;
  activeTab: TabKey;
  hasUnsavedChanges?: boolean;
  /** Dot warn en el tab cuando tiene cambios sin guardar. */
  hoursDirty?: boolean;
  servicesDirty?: boolean;
  /** Link público de reserva de la sucursal: undefined = cargando, null = sin tienda pública. */
  bookingUrl?: string | null;
  onClose: () => void;
  onTabChange(tab: TabKey): void;
  onEmployeeChange: (id: string | null) => void;
  onSaveHours: () => void;
  onSaveServices: () => void;
  onSelectedServiceIdsChange: (ids: string[]) => void;
  onCreateTimeOff: (input: ResourceTimeOffCreateInput) => void;
  onDeleteTimeOff: (id: string) => void;
  onRename: (name: string) => Promise<void>;
  savingRename: boolean;
  /** Persiste el color explícito elegido (p. ej. updateResource(id, { color })). */
  onUpdateColor?: (color: string) => Promise<void>;
  onPatchDay: (dayOfWeek: number, patch: Partial<ResourceHourDay>) => void;
  onAddShift: (dayOfWeek: number) => void;
  onRemoveShift: (dayOfWeek: number, index: number) => void;
  onShiftChange: (dayOfWeek: number, index: number, field: keyof ResourceHourShift, value: string) => void;
  onCopyFromStore: () => void;
  onApplyWeekdays: () => void;
  onDeactivate: () => void;
  onActivate: () => void;
};

export function AgendaAdminModal(props: AgendaAdminModalProps) {
  const { width: winW, height: winH } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const modalBackdropPadding = useModalBackdropPadding();
  const [personaPickerVisible, setPersonaPickerVisible] = useState(false);
  const [moreActionsVisible, setMoreActionsVisible] = useState(false);
  const [discardConfirmVisible, setDiscardConfirmVisible] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const nameRef = useRef<TextInput>(null);
  const [draftColor, setDraftColor] = useState('');
  const [colorTouched, setColorTouched] = useState(false);
  const [savingColor, setSavingColor] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!props.visible || !props.resource) return;
    setEditingName(false);
    setNameDraft(props.resource.displayName);
    setDraftColor(agendaDotColor(props.resource.color, props.resourceIndex));
    setColorTouched(false);
  }, [
    props.visible,
    props.resource?.id,
    props.resource?.displayName,
    props.resource?.color,
    props.resourceIndex,
  ]);

  useEffect(() => {
    if (editingName) nameRef.current?.focus();
  }, [editingName]);

  useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

  const nameDirty = editingName && nameDraft.trim() !== props.resource?.displayName;
  const canSaveName = nameDraft.trim().length > 0 && nameDirty && !props.savingRename;
  const colorDirty = colorTouched && draftColor !== (props.resource?.color ?? null);
  const canSaveColor =
    colorDirty && props.canManage && props.onUpdateColor != null && !savingColor;

  const requestClose = () => {
    if (props.hasUnsavedChanges || nameDirty || colorDirty) {
      setDiscardConfirmVisible(true);
      return;
    }
    props.onClose();
  };

  const employeeId = props.resource?.employeeId ?? null;
  const employeeName = props.agendaEmployees.find((e) => e.id === employeeId)?.name ?? null;
  const personaLabel = employeeName ?? 'Agenda libre';
  const staffOptions = props.agendaEmployees.map((e) => ({ id: e.id, name: e.name }));

  if (!props.resource) return null;

  const dotColor = agendaDotColor(props.resource.color, props.resourceIndex);

  const startNameEditing = () => {
    if (!props.canManage || props.savingRename) return;
    setNameDraft(props.resource?.displayName ?? '');
    setEditingName(true);
  };

  const cancelNameEditing = () => {
    if (props.savingRename) return;
    setNameDraft(props.resource?.displayName ?? '');
    setEditingName(false);
  };

  const saveName = async () => {
    const name = nameDraft.trim();
    if (!canSaveName) return;
    try {
      await props.onRename(name);
      setEditingName(false);
    } catch {
      return;
    }
  };

  const saveColor = async () => {
    if (!canSaveColor || !props.onUpdateColor) return;
    setSavingColor(true);
    try {
      await props.onUpdateColor(draftColor);
      setColorTouched(false);
    } catch {
      return;
    } finally {
      setSavingColor(false);
    }
  };

  const copyBookingLink = async () => {
    const url = props.bookingUrl;
    if (!url) return;
    try {
      await Clipboard.setStringAsync(url);
      setCopiedLink(true);
      showSuccess('Link copiado', 'Pegalo en WhatsApp, Instagram o donde quieras.');
      clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopiedLink(false), 2500);
    } catch {
      showError('No se pudo copiar', 'Seleccioná el link y copialo a mano.');
    }
  };

  const shareBookingLink = async () => {
    const url = props.bookingUrl;
    if (!url) return;
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && !navigator.share) {
        await copyBookingLink();
        return;
      }
      await Share.share({ message: url });
    } catch {
      return;
    }
  };

  const tabs: { key: TabKey; label: string; dirty?: boolean }[] = [
    { key: 'horarios', label: 'Horarios', dirty: props.hoursDirty },
    { key: 'servicios', label: 'Servicios', dirty: props.servicesDirty },
    { key: 'ausencias', label: 'Ausencias' },
    { key: 'reserva', label: 'Link de reserva' },
  ];

  const header = (
    <View style={styles.header}>
      <View style={[styles.dot, { backgroundColor: dotColor }]} />
      <View style={styles.headerMain}>
        {editingName ? (
          <View style={styles.nameEditRow}>
            <TextInput
              ref={nameRef}
              value={nameDraft}
              onChangeText={setNameDraft}
              style={styles.nameInput}
              maxLength={120}
              editable={!props.savingRename}
              placeholder="Nombre de la agenda"
              placeholderTextColor={agenda.ink3}
              returnKeyType="done"
              onSubmitEditing={() => void saveName()}
              accessibilityLabel="Nombre de la agenda"
            />
            <TouchableOpacity
              onPress={() => void saveName()}
              disabled={!canSaveName}
              style={[styles.nameAction, !canSaveName && styles.disabled]}
              accessibilityRole="button"
              accessibilityLabel="Guardar nombre de la agenda"
            >
              <Ionicons name="checkmark" size={20} color={canSaveName ? agenda.brand : agenda.ink3} />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={cancelNameEditing}
              disabled={props.savingRename}
              style={styles.nameAction}
              accessibilityRole="button"
              accessibilityLabel="Cancelar edición del nombre"
            >
              <Ionicons name="close" size={20} color={agenda.ink2} />
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.titleRow}>
            <Text style={styles.title} numberOfLines={1}>{props.resource.displayName}</Text>
            <TouchableOpacity
              onPress={startNameEditing}
              disabled={!props.canManage || props.savingRename}
              style={[styles.editNameButton, !props.canManage && styles.disabled]}
              accessibilityRole="button"
              accessibilityLabel="Editar nombre de la agenda"
            >
              <Ionicons name="create-outline" size={17} color={agenda.ink2} />
            </TouchableOpacity>
          </View>
        )}
        <Text style={styles.subtitle}>
          {props.resource.resourceKind === 'bay'
            ? 'Lift/box'
            : props.resource.resourceKind === 'equipment'
              ? 'Equipo'
              : 'Profesional'}
          {' · '}
          {props.resource.isActive ? 'Activa' : 'Inactiva — no entra en turnos nuevos'}
          {' · '}
          {employeeName ?? 'Sin persona asignada'}
        </Text>
      </View>
      <TouchableOpacity
        onPress={() => setMoreActionsVisible(true)}
        style={styles.headerAction}
        accessibilityRole="button"
        accessibilityLabel="Más acciones de la agenda"
      >
        <Ionicons name="ellipsis-horizontal" size={20} color={agenda.ink2} />
      </TouchableOpacity>
      <TouchableOpacity onPress={requestClose} style={styles.close} accessibilityRole="button" accessibilityLabel="Cerrar">
        <Ionicons name="close" size={22} color={agenda.ink2} />
      </TouchableOpacity>
    </View>
  );

  const personaPicker = (
    <View style={styles.personaRow}>
      <Text style={styles.personaLabel}>Persona asignada</Text>
      <TouchableOpacity
        style={styles.personaPicker}
        onPress={() => setPersonaPickerVisible(true)}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel="Cambiar persona asignada"
      >
        <View style={styles.personaMain}>
          <Text style={styles.personaValue}>{personaLabel}</Text>
        </View>
        <Ionicons name="chevron-down" size={18} color={agenda.ink2} />
      </TouchableOpacity>
    </View>
  );

  const tabBar = (
    <View style={styles.tabs}>
      {tabs.map((t) => (
        <TouchableOpacity
          key={t.key}
          style={[styles.tab, !props.isWide && styles.tabMobile, props.activeTab === t.key && styles.tabActive]}
          onPress={() => props.onTabChange(t.key)}
          accessibilityRole="tab"
          accessibilityState={{ selected: props.activeTab === t.key }}
          accessibilityLabel={t.dirty ? `${t.label}, cambios sin guardar` : t.label}
        >
          <Text style={[styles.tabText, props.activeTab === t.key && styles.tabTextActive]}>{t.label}</Text>
          {t.dirty ? <View style={styles.tabDirtyDot} /> : null}
        </TouchableOpacity>
      ))}
    </View>
  );

  const activeSave =
    props.activeTab === 'horarios'
      ? {
          onPress: props.onSaveHours,
          saving: props.savingHours,
          dirty: props.hoursDirty === true,
          label: 'Guardar horarios',
        }
      : props.activeTab === 'servicios'
        ? {
            onPress: props.onSaveServices,
            saving: props.savingServices,
            dirty: props.servicesDirty === true,
            label: 'Guardar servicios',
          }
        : null;

  const footer = (
    <View style={styles.footer}>
      <View style={[styles.saveRow, props.isWide && styles.saveRowRight]}>
        <TouchableOpacity
          onPress={requestClose}
          style={styles.cancel}
          disabled={activeSave?.saving ?? false}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        >
          <Text style={styles.cancelText}>Cerrar</Text>
        </TouchableOpacity>
        {activeSave ? (
          <TouchableOpacity
            onPress={activeSave.onPress}
            style={[
              styles.save,
              activeSave.saving && styles.disabled,
              !activeSave.dirty && !activeSave.saving && styles.saveMuted,
            ]}
            disabled={activeSave.saving || !activeSave.dirty}
            accessibilityRole="button"
            accessibilityLabel={
              activeSave.dirty ? activeSave.label : `${activeSave.label} (sin cambios)`
            }
            accessibilityState={{ disabled: activeSave.saving || !activeSave.dirty }}
          >
            <Text
              style={[
                styles.saveText,
                !activeSave.dirty && !activeSave.saving && styles.saveTextMuted,
              ]}
            >
              {activeSave.saving
                ? 'Guardando…'
                : activeSave.dirty
                  ? activeSave.label
                  : 'Sin cambios'}
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>

    </View>
  );

  const colorSection = (
    <View style={styles.colorSection}>
      <View style={styles.colorTitleRow}>
        <Text style={styles.sectionTitle}>Color de la agenda</Text>
        {canSaveColor ? (
          <TouchableOpacity
            onPress={() => void saveColor()}
            disabled={savingColor}
            style={styles.colorSave}
            accessibilityRole="button"
            accessibilityLabel="Guardar color de la agenda"
          >
            <Text style={styles.colorSaveText}>{savingColor ? 'Guardando…' : 'Guardar'}</Text>
          </TouchableOpacity>
        ) : null}
      </View>
      <View style={styles.swatches}>
        {AGENDA_DOT_FALLBACKS.map((c) => {
          const selected = draftColor === c;
          return (
            <TouchableOpacity
              key={c}
              style={[styles.swatch, { backgroundColor: c }, selected && styles.swatchOn]}
              onPress={() => {
                if (!props.canManage || !props.onUpdateColor) return;
                setDraftColor(c);
                setColorTouched(true);
              }}
              disabled={!props.canManage || !props.onUpdateColor || savingColor}
              accessibilityRole="button"
              accessibilityState={{ selected }}
              accessibilityLabel={`Color ${SWATCH_NAMES[c] ?? c}`}
            >
              {selected ? <Ionicons name="checkmark" size={15} color="#FFFFFF" /> : null}
            </TouchableOpacity>
          );
        })}
      </View>
      <Text style={styles.hint}>Identifica la agenda en el tablero, semana y picker.</Text>
    </View>
  );

  const horariosPanel = (
    <View>
      {colorSection}
      <Text style={styles.sectionTitle}>Horarios de atención</Text>
      <ResourceWeekHoursEditor
        days={props.hoursDraft}
        isWide={props.isWide}
        disabled={props.savingHours}
        hideCaption
        copyFromStoreLabel="Copiar horarios del local"
        showApplyWeekdays={false}
        onDayChange={props.onPatchDay}
        onAddShift={props.onAddShift}
        onRemoveShift={props.onRemoveShift}
        onShiftChange={props.onShiftChange}
        onCopyFromStore={props.onCopyFromStore}
        onApplyWeekdays={props.onApplyWeekdays}
      />
    </View>
  );

  const serviciosPanel = (
    <View>
      <Text style={styles.sectionTitle}>Servicios ofrecidos</Text>
      <ResourceServicesEditor
        allServices={props.allServices}
        selectedIds={props.selectedServiceIds}
        onChange={props.onSelectedServiceIdsChange}
        onSave={props.onSaveServices}
        saving={props.savingServices}
        hideSaveAction
        hideHint
        isWide={props.isWide}
      />
    </View>
  );

  const ausenciasPanel = (
    <View>
      <Text style={styles.sectionTitle}>Ausencias</Text>
      <ResourceTimeOffEditor
        items={props.timeOffList}
        loading={props.timeOffLoading}
        creating={props.creatingTimeOff}
        deleting={props.deletingTimeOff}
        hideCaption
        emptyLabel="Sin ausencias cargadas — trabaja todos los días de su horario."
        onCreate={props.onCreateTimeOff}
        onDelete={props.onDeleteTimeOff}
        isWide={props.isWide}
      />
    </View>
  );

  const reservaPanel = (
    <View>
      <Text style={styles.sectionTitle}>Link de reserva</Text>
      {props.bookingUrl === undefined ? (
        <Text style={styles.hint}>Cargando link…</Text>
      ) : props.bookingUrl === null ? (
        <Text style={styles.hint}>
          Tu tienda todavía no tiene link público. Activala desde Configuración → Tienda online.
        </Text>
      ) : (
        <>
          <View style={styles.linkBox}>
            <Text style={styles.linkUrl} numberOfLines={1} selectable>
              {props.bookingUrl.replace(/^https?:\/\//, '')}
            </Text>
            <TouchableOpacity
              onPress={() => void copyBookingLink()}
              style={styles.linkActionBtn}
              accessibilityRole="button"
              accessibilityLabel="Copiar link de reserva"
            >
              <Text style={styles.linkAction}>{copiedLink ? 'Copiado' : 'Copiar link'}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => void shareBookingLink()}
              style={styles.linkActionBtn}
              accessibilityRole="button"
              accessibilityLabel="Compartir link de reserva"
            >
              <Text style={styles.linkAction}>Compartir</Text>
            </TouchableOpacity>
          </View>
          <Text style={styles.hint}>Pegalo en Instagram o WhatsApp — tus clientes reservan solos.</Text>
        </>
      )}
    </View>
  );

  const body = (
    <ModalAwareScrollView style={styles.bodyScroll} keyboardShouldPersistTaps="handled" bottomOffset={24}>
      <View style={styles.body}>
        {props.activeTab === 'horarios' && horariosPanel}
        {props.activeTab === 'servicios' && serviciosPanel}
        {props.activeTab === 'ausencias' && ausenciasPanel}
        {props.activeTab === 'reserva' && reservaPanel}
      </View>
    </ModalAwareScrollView>
  );

  const moreActions: TicketMoreAction[] = [
    props.resource.isActive
      ? {
          key: 'deactivate',
          label: 'Desactivar agenda',
          hint: 'Deja de ofrecer nuevos turnos',
          icon: 'close-circle-outline',
          onPress: props.onDeactivate,
        }
      : {
          key: 'activate',
          label: 'Reactivar agenda',
          hint: 'Volverá a ofrecer nuevos turnos',
          icon: 'checkmark-circle-outline',
          onPress: props.onActivate,
        },
  ];

  const moreActionsSheet = (
    <TicketMoreActionsSheet
      visible={moreActionsVisible}
      title="Acciones de la agenda"
      actions={moreActions}
      onClose={() => setMoreActionsVisible(false)}
    />
  );

  const discardConfirm = (
    <OperationalConfirmModal
      visible={discardConfirmVisible}
      title="Descartar cambios"
      message="Hay cambios sin guardar en esta agenda. ¿Querés salir sin guardarlos?"
      confirmLabel="Descartar"
      destructive
      iconName="warning-outline"
      onClose={() => setDiscardConfirmVisible(false)}
      onConfirm={() => {
        setDiscardConfirmVisible(false);
        props.onClose();
      }}
    />
  );

  const personaPickerSheet = (
    <MasterFilterSheetModal
      visible={personaPickerVisible}
      title="Persona asignada"
      options={staffOptions}
      selectedId={employeeId}
      allOptionLabel="Agenda libre"
      showAllOption={true}
      appearance="ticket"
      onSelect={(id) => {
        props.onEmployeeChange(id);
        setPersonaPickerVisible(false);
      }}
      onClose={() => setPersonaPickerVisible(false)}
    />
  );

  if (props.isWide) {
    const modalMaxWidth = Math.min(720, winW - 48);
    return (
      <>
        <Modal visible={props.visible} transparent animationType="fade" onRequestClose={requestClose}>
          <View style={[posUi.modalBackdrop, modalBackdropPadding, { alignItems: 'center', justifyContent: 'center' }]}>
            <View style={[styles.dialog, { maxWidth: modalMaxWidth, height: winH * 0.88, maxHeight: winH * 0.88, width: '100%' }]}>
              {header}
              {personaPicker}
              {tabBar}
              {body}
              {footer}
            </View>
          </View>
        </Modal>
        {personaPickerSheet}
        {moreActionsSheet}
        {discardConfirm}
      </>
    );
  }

  return (
    <>
      <Modal visible={props.visible} transparent animationType="slide" onRequestClose={requestClose}>
        <View style={ticketUi.sheetBackdrop}>
          <Pressable style={StyleSheet.absoluteFill} onPress={requestClose} accessible={false} />
          <View style={[ticketUi.sheetCard, { paddingBottom: Math.max(insets.bottom, spacing.sm), flex: 1 }]}>
            <View style={ticketUi.grabber} />
            {header}
            {personaPicker}
            {tabBar}
            {body}
            {footer}
          </View>
        </View>
      </Modal>
      {personaPickerSheet}
      {moreActionsSheet}
      {discardConfirm}
    </>
  );
}

const styles = StyleSheet.create({
  dialog: {
    ...agendaPaperSheet,
    borderRadius: 14,
  },
  sheet: {
    backgroundColor: agenda.surface,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '92%',
    width: '100%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: agenda.border,
  },
  dot: { width: 12, height: 12, borderRadius: 6 },
  headerMain: { flex: 1, minWidth: 0 },
  titleRow: { flexDirection: 'row', alignItems: 'center', minWidth: 0, gap: 4 },
  title: { flexShrink: 1, fontSize: 17, fontWeight: '700', color: agenda.ink, fontFamily: agendaFonts.sans },
  editNameButton: { padding: 4 },
  nameEditRow: { flexDirection: 'row', alignItems: 'center', gap: 2 },
  nameInput: {
    flex: 1,
    minWidth: 0,
    height: 38,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: agenda.brand,
    borderRadius: agenda.radius,
    color: agenda.ink,
    backgroundColor: agenda.surface,
    fontSize: 16,
    fontWeight: '600',
    fontFamily: agendaFonts.sans,
  },
  nameAction: { padding: 6 },
  subtitle: { fontSize: 12, color: agenda.ink2, marginTop: 2, fontFamily: agendaFonts.sans },
  close: { padding: 4 },
  headerAction: { padding: 4, marginLeft: 2 },
  personaRow: {
    padding: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: agenda.border,
  },
  personaLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: agenda.ink3,
    marginBottom: 8,
    fontFamily: agendaFonts.sans,
  },
  personaPicker: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 10,
    borderRadius: agenda.radius,
    borderWidth: 1,
    borderColor: agenda.borderControl,
    backgroundColor: agenda.surface,
  },
  personaMain: { flex: 1 },
  personaValue: { fontSize: 14, fontWeight: '600', color: agenda.ink, fontFamily: agendaFonts.sans },

  tabs: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: agenda.border,
    paddingHorizontal: 16,
    gap: 4,
  },
  tab: {
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  tabActive: { borderBottomColor: agenda.brand },
  tabMobile: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  tabText: { fontSize: 13, fontWeight: '700', color: agenda.ink2, fontFamily: agendaFonts.sans },
  tabTextActive: { color: agenda.brand },
  tabDirtyDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: ticketColors.warn,
  },
  bodyScroll: { flex: 1 },
  body: { padding: 16, paddingBottom: 8 },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: agenda.ink,
    marginBottom: 8,
    fontFamily: agendaFonts.sans,
  },
  colorSection: { marginBottom: 16 },
  colorTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  colorSave: { paddingVertical: 4, paddingHorizontal: 2, marginBottom: 8 },
  colorSaveText: { fontSize: 13, fontWeight: '700', color: agenda.brand, fontFamily: agendaFonts.sans },
  /** Proto .swatches — paleta fija de dots de agenda. */
  swatches: { flexDirection: 'row', flexWrap: 'wrap', gap: 7 },
  swatch: {
    width: 36,
    height: 36,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  swatchOn: { borderColor: agenda.ink },
  hint: { fontSize: 12, color: agenda.ink2, marginTop: 6, fontFamily: agendaFonts.sans },
  /** Proto .link-box — caja con el link + copiar/compartir. */
  linkBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: agenda.surface2,
    borderWidth: 1,
    borderColor: agenda.borderControl,
    borderRadius: agenda.radius,
    paddingHorizontal: 11,
    paddingVertical: 8,
  },
  linkUrl: { flex: 1, minWidth: 0, fontSize: 12, color: agenda.ink2, fontFamily: agendaFonts.mono },
  linkActionBtn: { minHeight: 36, justifyContent: 'center', paddingHorizontal: 6 },
  linkAction: { fontSize: 12, fontWeight: '700', color: agenda.brand, fontFamily: agendaFonts.sans },
  footer: { padding: 16, paddingTop: 0 },
  saveRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  saveRowRight: { justifyContent: 'flex-end' },
  cancel: { paddingVertical: 10 },
  cancelText: { fontSize: 13, fontWeight: '700', color: agenda.ink2, fontFamily: agendaFonts.sans },
  save: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 18,
    borderRadius: agenda.radius,
    backgroundColor: agenda.brand,
    alignItems: 'center',
  },
  saveText: { fontSize: 13, fontWeight: '700', color: agenda.brandInk, fontFamily: agendaFonts.sans },
  saveMuted: { backgroundColor: agenda.surface2 },
  saveTextMuted: { color: agenda.ink3 },
  disabled: { opacity: 0.55 },
  deactivate: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 16,
    paddingVertical: 6,
    alignSelf: 'flex-start',
  },
  deactivateText: { fontSize: 13, fontWeight: '700', color: agenda.noShow.text, fontFamily: agendaFonts.sans },
});
